import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { doc, getDoc, collection, getDocs, getCountFromServer, updateDoc, writeBatch, query, where } from 'firebase/firestore';
import AvatarCropper from './AvatarCropper';
import Nav from './Nav';
import Top8Editor from './Top8Editor';
import { mergeTile, splitTile } from './tilePrivacy';
import { getConnection, sendConnect, acceptConnect, removeConnect, setBlock } from './connections';
import { loadVisibleRestrictedSocials, loadVisiblePhone } from './socials';
import { submitReport } from './reports';
import { isBirthdayToday } from './birthday';
import { platformLabel as getPlatformLabel } from './platforms';
import Onboarding from './Onboarding';
import { accentHex } from './accents';

// Accounts that always show both badges regardless of connection status
const OWNER_ACCOUNTS = ['corey'];

const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Entity category -> public label shown next to an entity account's name.
const ENTITY_LABELS = {
  business: 'Business', public_figure: 'Public Figure', artist: 'Artist',
  organization: 'Organization', venue: 'Venue', government: 'Government', nonprofit: 'Non-profit',
};

// What a tile is interactable for. Visitors can only ever act on YT8-linked
// tiles. The owner (viewing their own profile) keeps every shortcut.
// Keyed strictly on a real username, not on publicActionAvailable/primaryAction,
// because malformed legacy tiles can carry those flags without a real link.
function isYt8Tile(entry) {
  return !!(entry.yt8Username && String(entry.yt8Username).trim());
}

function getActionLabel(entry, isOwn) {
  if (isYt8Tile(entry)) {
    return entry.yt8Username ? `@${entry.yt8Username}` : 'Profile';
  }
  // Private actions are only labeled for the owner.
  if (!isOwn) return '';
  if (entry.primaryAction === 'TEXT') return 'Text';
  if (entry.primaryAction === 'SOCIAL') return getPlatformLabel(entry.primaryValue || '');
  return '';
}

// Whether this tile does anything when the current viewer taps it.
function isTileActionable(entry, isOwn) {
  if (isYt8Tile(entry)) return true;
  return isOwn && !!entry.primaryValue; // private actions: owner only
}

function handleTileTap(entry, isOwn, onViewProfile) {
  if (isYt8Tile(entry)) {
    onViewProfile(entry.yt8Username || entry.primaryValue);
    return;
  }
  if (!isOwn) return; // visitors cannot use private actions
  if (entry.primaryAction === 'TEXT') {
    window.open(`sms:${(entry.primaryValue || '').replace(/\D/g, '')}`, '_self');
  } else if (entry.primaryAction === 'SOCIAL') {
    window.open(entry.primaryValue, '_blank', 'noopener noreferrer');
  }
}

function PublicProfile({ currentUser, currentUserProfile }) {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [enriched, setEnriched] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [addedToTop8, setAddedToTop8] = useState(false);
  const [addBusy, setAddBusy]   = useState(false);
  const [addMsg, setAddMsg]     = useState('');
  const [replacePicker, setReplacePicker] = useState(null); // current top8 tiles to choose from when full
  const [connection, setConnection] = useState(null);
  const [restrictedSocials, setRestrictedSocials] = useState([]);
  const [phone, setPhone] = useState(null);
  const [connBusy, setConnBusy]     = useState(false);
  const [connCount, setConnCount]   = useState(0);
  const [blocked, setBlocked] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportType, setReportType]     = useState('profile'); // profile | photo | phone
  const [reportState, setReportState]   = useState(''); // '' | 'busy' | 'done'
  const avatarInputRef = useRef(null);
  const [bioEditing, setBioEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState('');
  const [savingHeader, setSavingHeader] = useState(false);
  const [inbound, setInbound] = useState(null);   // full breakdown — OWNER ONLY
  const [inboundCount, setInboundCount] = useState(null); // public count (everyone)
  const [showInbound, setShowInbound] = useState(false);
  const [prospectInfo, setProspectInfo] = useState(null); // tapped a non-linked tile as a visitor

  // Owner-only inline header edits (avatar + bio). Update the local profile copy
  // immediately; App's realtime listener refreshes currentUserProfile for Nav etc.
  const [cropSrc, setCropSrc] = useState(null);
  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropSrc(URL.createObjectURL(file));
    e.target.value = ''; // let them re-pick the same file later
  };
  const handleCroppedUpload = async (blob) => {
    if (!currentUser || !blob) { setCropSrc(null); return; }
    setSavingHeader(true);
    try {
      const r = storageRef(storage, `profile-pictures/${currentUser.uid}`);
      await uploadBytes(r, blob);
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, 'users', currentUser.uid), { photoURL: url });
      setProfile(prev => ({ ...prev, photoURL: url }));
    } catch (err) { console.error('Avatar update failed:', err); }
    setSavingHeader(false);
    setCropSrc(null);
  };

  const saveBio = async () => {
    if (!currentUser) return;
    setSavingHeader(true);
    try {
      const next = bioDraft.trim().slice(0, 160);
      await updateDoc(doc(db, 'users', currentUser.uid), { bio: next });
      setProfile(prev => ({ ...prev, bio: next }));
      setBioEditing(false);
    } catch (err) { console.error('Bio update failed:', err); }
    setSavingHeader(false);
  };

  const loadProfile = useCallback(async () => {
    setLoading(true); setNotFound(false); setProfile(null);
    try {
      const uSnap = await getDoc(doc(db, 'usernames', username.toLowerCase()));
      if (!uSnap.exists()) { setNotFound(true); setLoading(false); return; }
      const uid = uSnap.data().uid;
      const pSnap = await getDoc(doc(db, 'users', uid));
      if (!pSnap.exists()) { setNotFound(true); setLoading(false); return; }
      const data = { uid, ...pSnap.data() };
      // Owner accounts are always verified regardless of Firestore flag
      if (OWNER_ACCOUNTS.includes(data.username)) data.verified = true;
      setProfile(data);

      // The public flex count comes from a server-maintained connectionCount field
      // on the profile, so it shows to everyone (logged-out included) without
      // exposing the connection graph (whose list reads are sign-in-only).
      setConnCount(typeof data.connectionCount === 'number' ? data.connectionCount : 0);

      // Enrich registered-user entries with live profile data
      const entries = data.top8 || [];
      const enrichedEntries = await Promise.all(entries.map(async entry => {
        if (!entry.isRegisteredUser || !entry.yt8Username) return entry;
        try {
          const eSnap = await getDoc(doc(db, 'usernames', entry.yt8Username));
          if (!eSnap.exists()) return entry;
          const eUid = eSnap.data().uid;
          const eData = await getDoc(doc(db, 'users', eUid));
          if (!eData.exists()) return entry;
          const isOwner = OWNER_ACCOUNTS.includes(entry.yt8Username);
          // Per-pair get (not a list) so the connected badge still shows to
          // logged-out visitors: an accepted pair is publicly readable, but the
          // full graph can't be enumerated without auth.
          const conn = await getConnection(uid, eUid);
          const connected = conn?.status === 'accepted';
          return {
            ...entry,
            displayName: eData.data().displayName || entry.displayName,
            photoURL: eData.data().photoURL || entry.photoURL,
            verified: eData.data().verified || isOwner || false,
            birthday: eData.data().birthday || null,
            connectionStatus: (isOwner || connected) ? 'accepted' : (entry.connectionStatus || null),
          };
        } catch { return entry; }
      }));

      // If the viewer is the profile owner, merge their private action values
      // back in so their own tap shortcuts (text / social) still work.
      if (currentUser?.uid === uid) {
        try {
          const privSnap = await getDocs(collection(db, 'users', uid, 'privateTileActions'));
          const privMap = {};
          privSnap.forEach(d => { privMap[d.id] = d.data(); });
          setEnriched(enrichedEntries.map(e => mergeTile(e, privMap[e.id])));
        } catch {
          setEnriched(enrichedEntries);
        }
      } else {
        setEnriched(enrichedEntries);
      }
    } catch { setNotFound(true); }
    setLoading(false);
  }, [username, currentUser?.uid]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // How many Top 8s this person is in. The COUNT is a public flex shown on every
  // profile; the BREAKDOWN (who has them + ranks) is OWNER-ONLY. So for visitors we
  // pull only an aggregate count (their client never receives the breakdown); the
  // owner gets the full list for the modal.
  useEffect(() => {
    if (!profile?.username) { setInbound(null); setInboundCount(null); return; }
    let active = true;
    const qy = query(collection(db, 'users'), where('top8Usernames', 'array-contains', profile.username));
    const isOwner = !!(currentUser?.uid && profile.uid === currentUser.uid);
    if (isOwner) {
      const profileTop8 = new Set((profile.top8Usernames || []));
      getDocs(qy).then(snap => {
        const list = [];
        snap.forEach(d => {
          if (d.id === profile.uid) return;
          const data = d.data();
          const idx = (data.top8 || []).findIndex(t => t.yt8Username === profile.username);
          list.push({
            uid: d.id, username: data.username, displayName: data.displayName,
            photoURL: data.photoURL || null, rank: idx >= 0 ? idx + 1 : null,
            mutual: data.username ? profileTop8.has(data.username) : false,
          });
        });
        list.sort((a, b) => (a.rank || 99) - (b.rank || 99));
        if (active) { setInbound(list); setInboundCount(list.length); }
      }).catch(() => { if (active) { setInbound([]); setInboundCount(0); } });
    } else {
      setInbound(null);
      getCountFromServer(qy)
        .then(res => { if (active) setInboundCount(res.data().count); })
        .catch(() => { if (active) setInboundCount(null); });
    }
    return () => { active = false; };
  }, [profile, currentUser?.uid]);

  // Load the connection state between viewer and this profile.
  useEffect(() => {
    if (!currentUser || !profile || currentUser.uid === profile.uid) { setConnection(null); return; }
    let active = true;
    getConnection(currentUser.uid, profile.uid).then(c => { if (active) setConnection(c); });
    return () => { active = false; };
  }, [currentUser, profile]);

  // Securely load the restricted socials this viewer is allowed to see. Each
  // tier is a separate rules-checked query, so the client never sees socials it
  // isn't entitled to. Logged-out visitors get only the public ones.
  useEffect(() => {
    if (!profile?.uid || !currentUser) { setRestrictedSocials([]); return; }
    let active = true;
    const own = currentUser.uid === profile.uid;
    const connected = connection?.status === 'accepted';
    const inTop8 = !!profile?.top8?.some(t => t.yt8Username === currentUserProfile?.username);
    loadVisibleRestrictedSocials(profile.uid, { isOwn: own, isConnected: connected, inTop8 })
      .then(r => { if (active) setRestrictedSocials(r); });
    return () => { active = false; };
  }, [profile, connection, currentUser, currentUserProfile?.username]);

  // Load the phone this viewer is allowed to see. Public phones (business) come
  // off the profile doc; restricted ones are a single rules-checked read, so a
  // viewer who isn't entitled simply gets nothing. Runs for logged-out viewers
  // too (they can still see a public business number).
  useEffect(() => {
    if (!profile?.uid) { setPhone(null); return; }
    let active = true;
    loadVisiblePhone(profile.uid, profile).then(p => { if (active) setPhone(p); });
    return () => { active = false; };
  }, [profile, connection, currentUser]);

  // Whether I've blocked this profile.
  useEffect(() => {
    if (!currentUser?.uid || !profile?.uid || currentUser.uid === profile.uid) { setBlocked(false); return; }
    getDoc(doc(db, 'users', currentUser.uid, 'blocks', profile.uid))
      .then(s => setBlocked(s.exists())).catch(() => {});
  }, [currentUser?.uid, profile?.uid]);

  const handleBlock = async () => {
    if (!currentUser || !profile) return;
    const next = !blocked;
    if (next && !window.confirm(`Block @${profile.username}? They won't be able to connect with you, and any existing connection will be removed.`)) return;
    setBlockBusy(true);
    try {
      await setBlock(profile.uid, next);
      setBlocked(next);
      if (next) setConnection(null);
    } catch (e) { console.error('Block action failed:', e); }
    setBlockBusy(false);
  };

  const handleConnect = async () => {
    if (!currentUser || !profile || !currentUserProfile) return;
    setConnBusy(true);
    const me = {
      uid: currentUser.uid,
      username: currentUserProfile.username,
      displayName: currentUserProfile.displayName,
      photoURL: currentUserProfile.photoURL,
    };
    const other = { uid: profile.uid, username: profile.username, displayName: profile.displayName };
    try {
      if (!connection) {
        await sendConnect(me, other);
        setConnection({ users: [me.uid, other.uid], requestedBy: me.uid, status: 'pending' });
      } else if (connection.status === 'pending' && connection.requestedBy === profile.uid) {
        await acceptConnect(currentUser.uid, profile.uid, me);
        setConnection({ ...connection, status: 'accepted' });
      } else {
        // Cancel a request I sent, or disconnect an accepted connection.
        await removeConnect(currentUser.uid, profile.uid);
        setConnection(null);
      }
    } catch (e) {
      console.error('Connect action failed:', e);
    }
    setConnBusy(false);
  };

  // Returns the label + style for the connect button given current state.
  const connectButton = () => {
    if (!currentUser) return { label: 'Connect', accent: true, signIn: true };
    if (!connection) return { label: 'Connect', accent: true };
    if (connection.status === 'accepted') return { label: 'Connected', accent: false };
    if (connection.requestedBy === currentUser.uid) return { label: 'Requested', accent: false };
    return { label: 'Accept connection', accent: true };
  };

  // Whether this person is already in MY Top 8 (drives the button state).
  useEffect(() => {
    if (!profile?.username || !currentUserProfile) { setAddedToTop8(false); return; }
    setAddedToTop8((currentUserProfile.top8 || []).some(t => t.yt8Username === profile.username));
  }, [profile?.username, currentUserProfile]);

  // Add this person to my Top 8 from their profile (registered tile + connection
  // request), persisted with the same split as the editor. When the Top 8 is full,
  // open a picker so the user chooses which tile to replace.
  const addToMyTop8 = async (replaceTileId = null) => {
    if (!currentUser || !currentUserProfile || !profile || profile.uid === currentUser.uid) return;
    setAddBusy(true); setAddMsg('');
    try {
      const meRef = doc(db, 'users', currentUser.uid);
      const top8 = (await getDoc(meRef)).data()?.top8 || [];
      if (top8.some(t => t.yt8Username === profile.username)) { setAddedToTop8(true); setReplacePicker(null); setAddBusy(false); return; }
      if (top8.length >= 8 && !replaceTileId) { setReplacePicker(top8); setAddBusy(false); return; }

      let connectionStatus = 'pending';
      try {
        const existing = await getConnection(currentUser.uid, profile.uid);
        if (existing?.status) connectionStatus = existing.status;
        else {
          await sendConnect(
            { uid: currentUser.uid, username: currentUserProfile.username, displayName: currentUserProfile.displayName, photoURL: currentUserProfile.photoURL },
            { uid: profile.uid, username: profile.username, displayName: profile.displayName },
          );
        }
      } catch (e) { console.error('connect during add failed', e); }

      const fullTile = {
        id: genId(),
        displayName: profile.displayName || profile.username,
        photoURL: profile.photoURL || null,
        isRegisteredUser: true,
        yt8Username: profile.username,
        primaryAction: 'PROFILE',
        primaryValue: profile.username,
        connectionStatus,
        connections: [{ id: genId(), type: 'PROFILE', value: profile.username }],
      };
      const { publicTile, privateAction } = splitTile(fullTile);
      const batch = writeBatch(db);
      let nextTop8;
      if (replaceTileId) {
        const idx = top8.findIndex(t => t.id === replaceTileId);
        nextTop8 = [...top8];
        if (idx >= 0) {
          batch.delete(doc(db, 'users', currentUser.uid, 'privateTileActions', top8[idx].id));
          nextTop8[idx] = publicTile; // keep the replaced tile's slot
        } else {
          nextTop8.push(publicTile);
        }
      } else {
        nextTop8 = [...top8, publicTile];
      }
      const usernames = nextTop8.filter(t => t.yt8Username).map(t => t.yt8Username);
      batch.update(meRef, { top8: nextTop8, top8Usernames: usernames });
      batch.set(doc(db, 'users', currentUser.uid, 'privateTileActions', fullTile.id), privateAction);
      await batch.commit();
      setAddedToTop8(true);
      setReplacePicker(null);
    } catch (e) {
      console.error('Add to Top 8 failed:', e);
      setAddMsg('Could not add. Try again.');
    }
    setAddBusy(false);
  };
  const handleAddToTop8 = () => addToMyTop8(null);

  const submitReportNow = async () => {
    if (!currentUser || !profile || !reportReason.trim()) return;
    setReportState('busy');
    try {
      await submitReport({
        reporter: { uid: currentUser.uid, username: currentUserProfile?.username },
        target: { uid: profile.uid, username: profile.username },
        reason: reportReason,
        targetType: reportType,
      });
      setReportState('done');
      setReportReason('');
    } catch (e) {
      console.error('Report failed:', e);
      setReportState('');
    }
  };

  const isOwn = currentUserProfile?.username === username.toLowerCase();
  const slots = [...enriched, ...Array(Math.max(0, 8 - enriched.length)).fill(null)];

  // First-run flow: only the owner, only genuinely NEW accounts (created from
  // the launch of this feature onward), and only before they've completed it.
  // The account-age gate keeps existing testers from getting the first-run modal
  // they don't need. The localStorage guard hides it instantly on finish (before
  // the profile doc round-trips back through the realtime listener).
  const ONBOARDING_SINCE = Date.parse('2026-06-22T00:00:00Z');
  const createdMs = currentUserProfile?.createdAt?.toMillis?.()
    ?? (currentUserProfile?.createdAt?.seconds ? currentUserProfile.createdAt.seconds * 1000 : 0);
  let alreadyOnboarded = false;
  try { alreadyOnboarded = !!localStorage.getItem('yt8_onboarded'); } catch {}
  const showOnboarding = isOwn && currentUserProfile
    && createdMs >= ONBOARDING_SINCE
    && !currentUserProfile.onboardedAt && !alreadyOnboarded;

  // Public socials live on the (world-readable) profile doc; restricted ones
  // (connections / top8) are read from the secured restrictedSocials
  // subcollection in the effect above, so visibility is enforced by rules, not
  // by the client. Anything with a restricted visibility still on the public
  // doc (un-migrated data) is hidden here until the owner re-saves.
  const publicSocials = (profile?.socials || []).filter(s => !s.visibility || s.visibility === 'public');
  const seen = new Set(publicSocials.map(s => s.url));
  const visibleSocials = [...publicSocials, ...restrictedSocials.filter(s => !seen.has(s.url))];
  // A private phone is readable only by the owner; never surface it as a chip
  // (it would look exposed). It is managed in Settings instead.
  const visiblePhone = phone && phone.visibility !== 'private' ? phone : null;

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <div
        className={`wrap${(profile?.accentLevel || 'minimal') === 'max' ? ' profile--max' : ''}`}
        style={(profile?.accentLevel || 'minimal') !== 'none' && profile?.profileAccent
          ? { '--accent': accentHex(profile.profileAccent) } : undefined}
      >

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <span className="spinner" style={{ width: 32, height: 32 }} />
          </div>
        )}

        {!loading && notFound && (
          <div className="empty-state" style={{ paddingTop: 80 }}>
            <div className="empty-state-icon" style={{ fontSize: 14, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600, opacity: 0.3 }}>404</div>
            <h3>User not found</h3>
            <p>@{username} doesn't have a Your Top 8 account yet.</p>
            <Link to="/" className="btn btn-primary">Go Home</Link>
          </div>
        )}

        {!loading && profile && (
          <>
            {showOnboarding && (
              <Onboarding
                top8Count={currentUserProfile.top8?.length || 0}
                uid={currentUserProfile.uid}
                username={currentUserProfile.username}
                displayName={currentUserProfile.displayName}
              />
            )}
            {cropSrc && (
              <AvatarCropper src={cropSrc} onCancel={() => setCropSrc(null)} onSave={handleCroppedUpload} />
            )}
            {/* Profile header — matches index.html design exactly */}
            <header className="profile-header" aria-label="Profile header">
              {/* Wrapper (no clipping) so the edit badge can sit above the round,
                  overflow-hidden avatar frame instead of being cut off by it. */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div
                  className="profile-avatar-box"
                  style={isOwn ? { cursor: 'pointer' } : undefined}
                  onClick={isOwn ? () => avatarInputRef.current?.click() : undefined}
                  title={isOwn ? 'Change photo' : undefined}
                >
                  {profile.photoURL
                    ? <img src={profile.photoURL} alt={profile.displayName} />
                    : profile.displayName[0]?.toUpperCase()
                  }
                </div>
                {isOwn && (
                  <>
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      title="Change photo"
                      aria-label="Change photo"
                      style={{
                        position: 'absolute', right: -2, bottom: -2, width: 28, height: 28,
                        borderRadius: '50%', background: 'var(--accent)', color: '#060610',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, border: '2px solid var(--bg)', cursor: 'pointer', zIndex: 2,
                      }}
                    >{savingHeader ? '…' : '✎'}</button>
                    <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarPick} />
                  </>
                )}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span className="profile-name">{profile.displayName}</span>
                  {OWNER_ACCOUNTS.includes(profile.username) && (
                    <img src="/connected.png" alt="Connected" title="Connected" className="badge-8-img" />
                  )}
                  {profile.verified && (
                    <img src="/verified.png" alt="Verified" title="Verified" className="badge-8-img" />
                  )}
                  {profile.accountType === 'business' && (
                    <span
                      title="Entity account"
                      style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                        padding: '2px 7px', borderRadius: 999,
                        background: 'var(--card)', color: 'var(--accent)', border: '1px solid var(--accent)',
                      }}
                    >{ENTITY_LABELS[profile.entityCategory] || 'Business'}</span>
                  )}
                  {isBirthdayToday(profile.birthday) && (
                    <span
                      title="Birthday today"
                      style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                        padding: '2px 8px', borderRadius: 999,
                        background: '#FFC83D', color: '#3a2b00',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >🎂 Birthday</span>
                  )}
                </div>
                <div className="profile-handle">@{profile.username}</div>
                {isOwn ? (
                  <div style={{ marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <Link to="/people" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
                      {connCount} connection{connCount === 1 ? '' : 's'}
                    </Link>
                    {inbound && inbound.length > 0 && (
                      <button
                        onClick={() => setShowInbound(true)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)' }}
                      >
                        On {inbound.length} Top 8{inbound.length === 1 ? '' : 's'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    {connCount != null && (
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {connCount} connection{connCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {inboundCount != null && inboundCount > 0 && (
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                        On {inboundCount} Top 8{inboundCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                )}
                {isOwn ? (
                  bioEditing ? (
                    <div style={{ marginTop: 6 }}>
                      <textarea
                        className="input"
                        value={bioDraft}
                        onChange={e => setBioDraft(e.target.value)}
                        maxLength={160}
                        rows={2}
                        placeholder="A short description about yourself"
                        style={{ width: '100%', resize: 'vertical' }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={saveBio} disabled={savingHeader}>Save</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setBioEditing(false)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="profile-bio"
                      style={{ marginTop: 6, cursor: 'pointer' }}
                      onClick={() => { setBioDraft(profile.bio || ''); setBioEditing(true); }}
                      title="Edit bio"
                    >
                      {profile.bio || <span style={{ color: 'var(--muted)' }}>Add a bio ✎</span>}
                    </div>
                  )
                ) : (
                  profile.bio && <div className="profile-bio" style={{ marginTop: 6 }}>{profile.bio}</div>
                )}

                {/* User's own social links + phone (filtered by per-link visibility) */}
                {(visibleSocials.length > 0 || visiblePhone) && (
                  <div className="profile-socials" aria-label="Contact links">
                    {visibleSocials.map((s, i) => (
                      <a
                        key={i}
                        className="soc"
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getPlatformLabel(s.url)}
                      </a>
                    ))}
                    {visiblePhone && (
                      <a className="soc" href={`tel:${visiblePhone.value.replace(/[^\d+]/g, '')}`} title="Call">
                        {visiblePhone.value}
                      </a>
                    )}
                  </div>
                )}

                {/* Connect + Share — only on other people's profiles */}
                {!isOwn && (
                  <div className="profile-socials" style={{ marginTop: 14 }}>
                    {!blocked && (() => {
                      const cb = connectButton();
                      if (cb.signIn) {
                        return <Link to="/" className="soc-action soc-action-join">Connect</Link>;
                      }
                      return (
                        <button
                          className={`soc-action ${cb.accent ? 'soc-action-join' : ''}`}
                          onClick={handleConnect}
                          disabled={connBusy}
                          title={cb.label === 'Connected' ? 'Click to disconnect' : cb.label === 'Requested' ? 'Click to cancel request' : cb.label}
                        >
                          {cb.label === 'Connected' ? '✓ Connected' : cb.label}
                        </button>
                      );
                    })()}
                    {currentUser ? (
                      <button
                        className="soc-action"
                        onClick={handleAddToTop8}
                        disabled={addBusy || addedToTop8}
                        title={addedToTop8 ? 'Already in your Top 8' : 'Add to your Top 8'}
                      >{addedToTop8 ? '✓ In your Top 8' : '+ Add to Top 8'}</button>
                    ) : (
                      <Link to="/" className="soc-action soc-action-join">Add to Top 8</Link>
                    )}
                    {currentUser && (
                      <button
                        className="soc-action"
                        onClick={() => { setReportOpen(true); setReportState(''); }}
                        title="Report this profile"
                      >Report</button>
                    )}
                    {currentUser && (
                      <button
                        className="soc-action"
                        onClick={handleBlock}
                        disabled={blockBusy}
                        title={blocked ? 'Unblock this user' : 'Block this user'}
                      >{blocked ? 'Unblock' : 'Block'}</button>
                    )}
                  </div>
                )}
                {!isOwn && addMsg && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>{addMsg}</div>
                )}
              </div>
            </header>

            {replacePicker && (
              <div className="modal-overlay" onClick={() => !addBusy && setReplacePicker(null)}>
                <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Your Top 8 is full</h3>
                  <p style={{ fontSize: 13, color: 'var(--muted)', margin: '6px 0 12px' }}>
                    Pick who {profile.displayName} should replace.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '50vh', overflowY: 'auto' }}>
                    {replacePicker.map((t, i) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => addToMyTop8(t.id)}
                        disabled={addBusy}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', cursor: 'pointer', borderRadius: 8, background: 'none', border: 'none', textAlign: 'left', fontFamily: 'var(--font)', color: 'var(--text)' }}
                      >
                        <span style={{ fontWeight: 800, color: 'var(--accent)', width: 22, flexShrink: 0 }}>#{i + 1}</span>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', background: 'var(--card-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--accent)' }}>
                          {t.photoURL ? <img src={t.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (t.displayName?.[0]?.toUpperCase() || '?')}
                        </div>
                        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.displayName}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>Replace</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                    <button className="btn btn-ghost" onClick={() => setReplacePicker(null)} disabled={addBusy}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {isOwn && showInbound && inbound && (
              <div className="modal-overlay" onClick={() => setShowInbound(false)}>
                <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Where you stand</h3>
                  <p style={{ fontSize: 13, color: 'var(--muted)', margin: '6px 0 12px' }}>
                    You're on {inbound.length} Top 8{inbound.length === 1 ? '' : 's'}.
                    {(() => {
                      const counts = {};
                      inbound.forEach(p => { if (p.rank) counts[p.rank] = (counts[p.rank] || 0) + 1; });
                      const parts = Object.keys(counts).map(Number).sort((a, b) => a - b).map(r => `${counts[r]} at #${r}`);
                      return parts.length ? ' ' + parts.join(' · ') : '';
                    })()}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '50vh', overflowY: 'auto' }}>
                    {inbound.map(p => (
                      <div
                        key={p.uid}
                        onClick={() => { setShowInbound(false); navigate(`/${p.username}`); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer', borderRadius: 8 }}
                      >
                        <div style={{ width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', background: 'var(--card-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--accent)' }}>
                          {p.photoURL ? <img src={p.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.displayName?.[0]?.toUpperCase() || '?')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.displayName}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{p.username}{p.mutual ? ' · ↔ you have them too' : ''}</div>
                        </div>
                        <span style={{ fontWeight: 800, color: 'var(--accent)', flexShrink: 0 }}>#{p.rank ?? '?'}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <button className="btn btn-primary" onClick={() => setShowInbound(false)}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {reportOpen && (
              <div className="modal-overlay" onClick={() => setReportOpen(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Report this profile</h3>
                  {reportState === 'done' ? (
                    <>
                      <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 8 }}>
                        Thanks. We'll review it.
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <button className="btn btn-primary" onClick={() => setReportOpen(false)}>Close</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '6px 0 10px' }}>
                        Tell us what's wrong with @{profile.username}'s profile (impersonation, your photo used without permission, abuse, etc.).
                      </p>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                        {[['profile', 'Profile'], ['photo', 'Photo'], ['phone', 'Phone']].map(([v, label]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setReportType(v)}
                            style={{
                              fontSize: 12, padding: '5px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font)',
                              border: `1px solid ${reportType === v ? 'var(--accent)' : 'var(--border)'}`,
                              background: reportType === v ? 'var(--accent)' : 'transparent',
                              color: reportType === v ? '#060610' : 'var(--muted)',
                              fontWeight: reportType === v ? 700 : 400,
                            }}
                          >{label}</button>
                        ))}
                      </div>
                      <textarea
                        className="input"
                        value={reportReason}
                        onChange={e => setReportReason(e.target.value)}
                        placeholder="What's the problem?"
                        rows={4}
                        style={{ width: '100%', resize: 'vertical' }}
                        maxLength={2000}
                      />
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                        <button className="btn btn-ghost" onClick={() => setReportOpen(false)}>Cancel</button>
                        <button
                          className="btn btn-primary"
                          onClick={submitReportNow}
                          disabled={!reportReason.trim() || reportState === 'busy'}
                        >
                          {reportState === 'busy' ? 'Sending…' : 'Submit report'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {prospectInfo && (
              <div className="modal-overlay" onClick={() => setProspectInfo(null)}>
                <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, textAlign: 'center' }}>
                  <p style={{ fontSize: 15, margin: '4px 0 0', fontWeight: 600 }}>No profile to open here.</p>
                  {!currentUser && (
                    <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
                      Want a Top 8 of your own?
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
                    {!currentUser && <Link to="/" className="btn btn-primary">Make your own Top 8</Link>}
                    <button className="btn btn-secondary" onClick={() => setProspectInfo(null)}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* Your own profile is always editable in place; visitors get the
                clean view. */}
            {isOwn ? (
              <Top8Editor
                currentUser={currentUser}
                currentUserProfile={currentUserProfile}
                embedded
                coachAdd={showOnboarding && (currentUserProfile.top8?.length || 0) === 0}
              />
            ) : (
            <div className="grid-card">
              <div className="grid-head">
                <h2>{`${profile.displayName.split(' ')[0]}'s`} Top 8</h2>
              </div>

              {enriched.length === 0 ? (
                <div className="empty-state" style={{ padding: '32px 0' }}>
                  <div className="empty-state-icon" />
                  <h3>No Top 8 yet</h3>
                  <p>{profile.displayName} hasn't set up their Top 8 yet.</p>
                </div>
              ) : (
                <div className="top8" data-layout={profile.top8Layout || 'grid4'}>
                  {slots.map((entry, idx) => {
                    if (!entry) {
                      return (
                        <div key={`empty-${idx}`} className="tile placeholder">
                          <div className="tile-img">
                            <div className="plus" aria-hidden="true">+</div>
                          </div>
                          <div className="tile-body">
                            <div className="tile-name" style={{ color: '#666' }}>—</div>
                          </div>
                        </div>
                      );
                    }
                    const actionable = isTileActionable(entry, isOwn);
                    // A visitor tapping a non-linked tile (a prospect) gets a
                    // neutral note instead of a dead click. Cursor stays default
                    // so it doesn't look like a broken link.
                    const prospectTap = !actionable && !isOwn && !isYt8Tile(entry);
                    const isBday = isBirthdayToday(entry.birthday);
                    return (
                      <div
                        key={entry.id}
                        className="tile"
                        style={{ cursor: actionable ? 'pointer' : 'default' }}
                        onClick={
                          actionable ? () => handleTileTap(entry, isOwn, (u) => navigate(`/${u}`))
                          : prospectTap ? () => setProspectInfo(entry)
                          : undefined
                        }
                        title={getActionLabel(entry, isOwn)}
                      >
                        <div className="tile-img">
                          {/* Prospect photos are owner-uploaded, so visitors never
                              see them (covers legacy tiles still carrying a URL). */}
                          {(entry.photoURL && (isOwn || entry.isRegisteredUser))
                            ? <img src={entry.photoURL} alt={entry.displayName} />
                            : <div style={{
                                width: '100%', height: '100%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 'clamp(28px,6vw,48px)', fontWeight: 800,
                                color: 'var(--accent)', background: 'var(--card-2)',
                              }}>
                                {entry.displayName[0]?.toUpperCase()}
                              </div>
                          }
                          <span className="rank-badge">{idx + 1}</span>
                          {entry.connectionStatus === 'accepted' && (
                            <img src="/connected.png" alt="Connected" title="Connected on Your Top 8"
                              className={`tile-badge-8-img${entry.verified ? ' tile-badge-8-img--paired' : ''}`} />
                          )}
                          {entry.verified && (
                            <img src="/verified.png" alt="Verified" title="Verified" className="tile-badge-8-img" />
                          )}
                          {isBday && (
                            <div style={{
                              position: 'absolute', inset: 0, borderRadius: 'inherit',
                              border: '3px solid #FFC83D', boxShadow: 'inset 0 0 10px rgba(255,200,61,0.55)',
                              pointerEvents: 'none', zIndex: 3,
                            }} />
                          )}
                        </div>
                        <div className="tile-body">
                          <span className="tile-name">{entry.displayName}</span>
                          <div className="tile-link">{getActionLabel(entry, isOwn)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* CTA for non-users */}
            {!currentUser && (
              <div style={{
                marginTop: 22, padding: '16px 20px',
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', display: 'flex',
                alignItems: 'center', gap: 14,
              }}>
                <img src="/yt8.png" alt="Your Top 8" style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', marginBottom: 3 }}>Make your own Top 8</strong>

                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Show the world who matters most, and get a live home screen widget.
                  </span>
                </div>
                <Link to="/" className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>Join Free</Link>
              </div>
            )}

            <footer>
              <div>Your Top 8 is independent, not affiliated with any other social network.</div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 20 }}>
                <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>Privacy</Link>
                <Link to="/terms"   style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>Terms</Link>
              </div>
            </footer>
          </>
        )}
      </div>
    </>
  );
}

export default PublicProfile;
